import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { getArticle, articles, AUTHOR } from "../data/articles";
import { Clock, ArrowLeft, User } from "lucide-react";

export default function ResourceArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return (
      <SiteLayout>
        <SEO title="Article Not Found" description="This article could not be located. Browse all of our mental-health resources." path={`/resources/${slug}`} noindex />
        <section className="container-pad max-w-3xl mx-auto py-20 text-center">
          <h1 className="font-heading text-3xl font-semibold text-ink">Article not found.</h1>
          <p className="mt-3 text-muted_text">The link may be outdated. Explore our full library instead.</p>
          <Link to="/resources" className="btn-gold !text-sm mt-8 inline-flex">Back to Resources</Link>
        </section>
      </SiteLayout>
    );
  }

  const url = `https://goldenlifesh.com/resources/${article.slug}`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: { "@type": "Organization", name: AUTHOR.name, url: AUTHOR.url },
    publisher: {
      "@type": "MedicalOrganization",
      name: "Golden Life Standard Health",
      logo: { "@type": "ImageObject", url: "https://goldenlifesh.com/brand/logo.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <SiteLayout>
      <SEO title={article.title} description={article.description} path={`/resources/${article.slug}`} noBrand />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />

      <article className="container-pad max-w-3xl mx-auto pt-14 lg:pt-20 pb-16">
        <Link
          to="/resources"
          data-testid="article-back"
          className="inline-flex items-center gap-1 text-sm text-sage-700 hover:text-gold-700 font-medium mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> All resources
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {article.tags?.map((t) => (
            <span key={t} className="text-[10px] tracking-[0.16em] uppercase font-semibold text-sage-700">{t}</span>
          ))}
        </div>
        <h1 data-testid="article-title" className="font-heading text-4xl sm:text-5xl font-semibold text-ink tracking-tight leading-[1.08]">
          {article.title}
        </h1>
        <p className="mt-5 text-lg text-muted_text leading-relaxed">{article.excerpt}</p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted_text pb-8 border-b border-[#E8EBE4]">
          <span className="inline-flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" strokeWidth={1.75} /> {AUTHOR.name}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.75} /> {article.readingMinutes} min read
          </span>
          <time dateTime={article.publishedAt}>Published {format(new Date(article.publishedAt), "MMM d, yyyy")}</time>
        </div>

        <div data-testid="article-body" className="mt-10 space-y-10">
          {article.sections.map((s, i) => (
            <section key={i} data-testid={`article-section-${i}`}>
              <h2 className="font-heading text-2xl font-semibold text-ink tracking-tight">{s.heading}</h2>
              <div className="mt-4 space-y-4">
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="text-base text-ink/85 leading-[1.75]">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-[#E8EBE4] text-xs text-muted_text leading-relaxed">
          <p><strong className="text-ink">Educational content only.</strong> This article is not medical advice, does not establish a clinician-patient relationship, and is not a substitute for a personal evaluation by a licensed clinician. If you are in crisis, call or text 988, or dial 911.</p>
        </div>
      </article>

      {related.length > 0 && (
        <section data-testid="article-related" className="container-pad max-w-7xl mx-auto pb-16">
          <h2 className="font-heading text-2xl font-semibold text-ink mb-6">More from our clinical team</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                data-testid={`related-card-${a.slug}`}
                className="card-soft p-6 block hover:shadow-md hover:-translate-y-0.5 transition"
              >
                <p className="text-[10px] tracking-[0.16em] uppercase font-semibold text-sage-700 mb-2">{a.tags?.[0]}</p>
                <p className="font-heading text-lg font-semibold text-ink leading-snug">{a.title}</p>
                <p className="mt-3 text-xs text-muted_text inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.75} /> {a.readingMinutes} min read
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTABanner
        eyebrow="Ready to talk?"
        title="Reading only takes you so far."
        body="A conversation with a real clinician usually does what an article cannot. First evaluations are 60 minutes, telehealth, and typically booked within a week."
      />
    </SiteLayout>
  );
}
